import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback } from "react";
import { requestApprovalMultiSizePopupKey } from "../../../services/LocalStorageService";
import { trackEvent } from "../../../utils/analytics";
import { type OpenShiftRequest, type ReplaceShiftRequest, ShiftType } from "../../../utils/models";
import { isRequestInstanceOfReplaceShiftRequest } from "../../../utils/utilFunctions";
import { usePerformGlobalRefresh } from "../../header/hooks";
import { ApproveRequestOutcome } from "../../RequestData/Provider/RequestUtilsProvider";
import { useRequestUtils } from "../../RequestData/Provider/useRequestUtils";
import { MultiSizePopup } from "../../UIToolkit/Popups/MultiSizePopup/MultiSizePopup";
import { MultiSizePopupState } from "../../UIToolkit/Popups/MultiSizePopup/utils";
import { useDenyNurseFromRequest } from "./hooks/useDenyNurseFromRequest";
import { MinimizedRequestApproval } from "./MinimizedRequestApproval/MinimizedRequestApproval";
import { RequestApprovalDialog } from "./RequestApprovalDialog";
import { useGetApprovalTitle } from "./useGetApprovalTitle";

type Props = {
	open: boolean;
	onClose: () => void;
	request: OpenShiftRequest | ReplaceShiftRequest;
	isRecommendation?: boolean;
	shouldNotAllowToMinimize?: boolean;
	shouldNotAllowToMaximize?: boolean;
	popupLocalStorageKey?: string;
};

export const NormalizedRequestApprovalDialog: FC<Props> = ({
	request,
	open,
	onClose,
	isRecommendation,
	shouldNotAllowToMinimize = false,
	shouldNotAllowToMaximize = false,
	popupLocalStorageKey = requestApprovalMultiSizePopupKey,
}) => {
	const { approveRequest, isApproveAddRequestLoading, recomendationFromRequests } = useRequestUtils();
	const issue = recomendationFromRequests(request);
	const neededNursesAmount = isRequestInstanceOfReplaceShiftRequest(request) ? 1 : request.needed_nurses;
	const { performGlobalRefresh } = usePerformGlobalRefresh({});
	const { queueDeny, queueRestore, queuedNurseIds, flushDenies, clearQueue } = useDenyNurseFromRequest(request.id);

	const handleClose = useCallback(() => {
		clearQueue();
		onClose();
	}, [clearQueue, onClose]);

	const handleOnSubmit = async (suggestionIndex: number, nurse_id?: number, location?: string) => {
		trackEvent(`${isRecommendation ? "recommendation" : "request"}_approval_submitted`, {
			request_id: request.id,
			suggestion_index: suggestionIndex,
			nurse_id,
			queued_deny_count: queuedNurseIds.length,
		});

		// Commit queued denies first so the backend state matches what the user saw before they
		// approved (the approved nurse can never be one that's also queued for deny — the UI
		// marks queued nurses as pending and blocks selection).
		await flushDenies();

		// Only approve when an explicit nurse_id was passed. Falling back to issue.suggestions
		// here would silently approve the first suggestion on a deny-only submit.
		const outcome = nurse_id ? await approveRequest(request, nurse_id, location) : null;
		await performGlobalRefresh({
			avoidRefreshFor: {
				notificationsData: true,
				nursesData: true,
				// Every approval write reloads the schedule itself, and a deny doesn't change it; reloading it here
				// again rebuilt the whole schedule a second time per approval.
				departmentData: true,
				// approveRequest reloads the requests once it applied or dismissed; a deny-only submit reloads nothing.
				requestsData: outcome === ApproveRequestOutcome.Applied || outcome === ApproveRequestOutcome.Dismissed,
			},
		});
		onClose();
	};

	const title = useGetApprovalTitle({
		request,
		neededNursesAmount,
	});

	const maximizedContent = (
		<RequestApprovalDialog
			open={open}
			onClose={handleClose}
			issue={issue}
			requestShiftTimes={issue.shiftTimes}
			requestDate={issue.shiftTimes?.date ?? DateTime.now()}
			requestShift={issue.suggestions[0]?.shiftNo === 0 ? ShiftType.Day : ShiftType.Night}
			requestId={request.id}
			neededNurses={neededNursesAmount}
			onSubmit={(index: number, nurse_id?: number, location?: string) => handleOnSubmit(index, nurse_id, location)}
			request={request}
			contentOnly={!shouldNotAllowToMinimize}
			bonus={request.current_bonus ?? request.bonus}
			onDenyNurse={queueDeny}
			onRestoreNurse={queueRestore}
			queuedDenyNurseIds={queuedNurseIds}
		/>
	);

	return open ? (
		shouldNotAllowToMinimize ? (
			maximizedContent
		) : (
			<MultiSizePopup
				popupLocalStorageKey={popupLocalStorageKey}
				title={title}
				open={open}
				onClose={handleClose}
				maxContentWidth={"1200px"}
				disableMaximize={shouldNotAllowToMaximize}
				bodyRenderer={(currentSizeState) =>
					currentSizeState === MultiSizePopupState.MINIMIZED ? (
						<MinimizedRequestApproval
							onSubmit={(_, suggestionIndex, nurseId, location) => handleOnSubmit(suggestionIndex, nurseId, location)}
							onCancel={handleClose}
							request={request}
							issue={issue}
							isLoading={isApproveAddRequestLoading}
							onDenyNurse={queueDeny}
							onRestoreNurse={queueRestore}
							queuedDenyNurseIds={queuedNurseIds}
						/>
					) : currentSizeState === MultiSizePopupState.MAXIMIZED ? (
						<Box
							sx={{
								minWidth: "1200px",
								px: 1,
							}}
						>
							{maximizedContent}
						</Box>
					) : null
				}
			/>
		)
	) : null;
};
